import { Card, Pagination } from '@amelio/ui-library';
import { useState, useEffect } from 'react';

import { CommentItem } from './CommentItem';

import type { QuestionComment } from './types';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';

interface CommentSectionProps {
    comments: QuestionComment[];
    onReplySubmit?: (reviewQuestionAnswerId: string, content: string) => Promise<void> | void;
    dataTestId?: string;
    isSubmittingReply?: boolean;
}

const PAGE_SIZE = 10;

export const CommentSection = ({
    comments,
    onReplySubmit,
    dataTestId = 'comment-section',
    isSubmittingReply = false,
}: CommentSectionProps) => {
    const { t } = useAmelioTranslations();

    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [comments]);

    const totalCount = comments.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const visibleComments = comments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (comments.length === 0) {
        return null;
    }

    return (
        <Card
            className={'mt-6 flex flex-col items-center'}
            padding={'sm'}
            borderRadius={'2xl'}
        >
            <div className={'mt-2 w-full space-y-3 overflow-hidden rounded-[15px]'}>
                {visibleComments.map((comment: QuestionComment) => (
                    <CommentItem
                        key={comment.id}
                        comment={comment}
                        dataTestId={`${dataTestId}-comment-${comment.id}`}
                        onReplySubmit={onReplySubmit}
                        isSubmittingReply={isSubmittingReply}
                    />
                ))}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={PAGE_SIZE}
                    onPageChange={setCurrentPage}
                    dataTestId={`${dataTestId}-pagination`}
                    resultsSummaryText={t('common.showingResults', {
                        start: (currentPage - 1) * PAGE_SIZE + 1,
                        end: Math.min(currentPage * PAGE_SIZE, totalCount),
                        total: totalCount,
                    })}
                />
            </div>
        </Card>
    );
};
